import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ListChecks } from "lucide-react";
import type { JobRow } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { useEventListener } from "../lib/events.tsx";
import { jobTitle, paramSummary } from "../lib/labels.ts";
import { jobDuration, shortTime } from "../lib/release.ts";
import { AppIcon } from "../components/AppIcon.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Card, Check, Chips, Empty, ErrorState, Field, RowLink, StatusPill } from "../components/Ui.tsx";

const ACTIVE = new Set(["queued", "claimed", "running"]);

const FILTERS = ["all", "running", "failed", "succeeded"] as const;
type Filter = (typeof FILTERS)[number];

const FILTER_LABELS: Record<Filter, string> = {
  all: "Tümü",
  running: "Çalışan",
  failed: "Başarısız",
  succeeded: "Başarılı",
};

function asFilter(v: string | null): Filter {
  return (FILTERS as readonly string[]).includes(v ?? "") ? (v as Filter) : "all";
}

/**
 * Tüm işler, uygulamalar arası. Filtreler adres çubuğunda: kenar çubuğundaki
 * "başarısız" rozeti ve uygulama sayfasındaki "Tümünü gör" doğrudan filtreli açar.
 *
 * Sağlık gözcüsünün 5 dakikada bir attığı otomatik kontroller varsayılan gizli;
 * yoksa elle başlatılan işler onların arasında kaybolur.
 */
export function JobsPage(): ReactNode {
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();
  const status = asFilter(search.get("status"));
  const app = search.get("app") ?? "";
  const [showAuto, setShowAuto] = useState(false);

  function setFilter(key: "status" | "app", value: string): void {
    const next = new URLSearchParams(search);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setSearch(next, { replace: true });
  }

  const apps = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });

  const jobs = useQuery({
    queryKey: ["jobs", "list", status, app],
    queryFn: () =>
      client.jobs({
        // "Çalışan" üç durumu kapsıyor (kuyrukta/başlıyor/çalışıyor); sunucu tek durum
        // süzdüğü için onu istemcide ayırıyoruz.
        ...(status === "failed" || status === "succeeded" ? { status } : {}),
        ...(app ? { app } : {}),
        limit: 100,
      }),
    refetchInterval: (q) => (q.state.data?.some((j) => ACTIVE.has(j.status)) ? 5000 : false),
  });

  // Yeni başlayan işler de listeye düşsün (aralık yalnızca aktif iş varken çalışıyor).
  useEventListener((event) => {
    if (event.t === "job.update") void queryClient.invalidateQueries({ queryKey: ["jobs", "list"] });
  }, []);

  const appName = (id: string | null): string =>
    id ? (apps.data?.find((a) => a.id === id)?.displayName ?? id) : "sistem";

  const byStatus = (jobs.data ?? []).filter((j) =>
    status === "running" ? ACTIVE.has(j.status) : status === "all" ? true : j.status === status,
  );
  const visible = showAuto ? byStatus : byStatus.filter((j) => j.createdBy !== "watchdog");
  const hidden = byStatus.length - visible.length;

  return (
    <>
      <PageHeader
        kicker="kayıtlar"
        title="İşler"
        meta={jobs.data ? `${visible.length} iş gösteriliyor${hidden ? ` · ${hidden} otomatik kontrol gizli` : ""}` : undefined}
      />
      <PageBody>
        <div className="card grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,240px)] lg:items-end">
          <Field label="Durum">
            <Chips values={FILTERS} labels={FILTER_LABELS} value={status} onChange={(v) => setFilter("status", v)} />
          </Field>
          {apps.error ? (
            <ErrorState error={apps.error} onRetry={() => void apps.refetch()} compact />
          ) : (
            <Field label="Uygulama">
              <select
                className="input"
                value={app}
                disabled={apps.isLoading}
                onChange={(e) => setFilter("app", e.target.value)}
              >
                <option value="">{apps.isLoading ? "Yükleniyor…" : "Tüm uygulamalar"}</option>
                {apps.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className="lg:col-span-2">
            <Check label="Otomatik kontrolleri göster" checked={showAuto} onChange={setShowAuto} />
          </div>
        </div>

        <Card title="Son işler" icon={ListChecks} hint="yeniden eskiye · en fazla 100" flush>
          {jobs.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : jobs.error ? (
            <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />
          ) : visible.length ? (
            visible.map((job) => <JobLine key={job.id} job={job} appName={appName(job.appId)} />)
          ) : (
            <Empty>
              {hidden
                ? `Bu filtreyle elle başlatılmış iş yok (${hidden} otomatik kontrol gizli).`
                : "Bu filtreyle iş yok."}
            </Empty>
          )}
        </Card>
      </PageBody>
    </>
  );
}

function JobLine({ job, appName }: { job: JobRow; appName: string }): ReactNode {
  // Uygulamasız (sistem) işlerde logo yok; hizayı boş kutucuk korur.
  const params = paramSummary(job.params);
  const duration = jobDuration(job);
  return (
    <RowLink to={`/jobs/${job.id}`} className="items-start">
      <span className="mt-[1px] shrink-0">
        <StatusPill status={job.status} />
      </span>
      {job.appId ? (
        <AppIcon id={job.appId} name={appName} size={22} className="mt-[1px]" />
      ) : (
        <span className="size-[22px] shrink-0" aria-hidden />
      )}
      <span className="grid min-w-0 flex-1 gap-0.5">
        <span className="truncate text-sm text-neutral-100">{jobTitle(job.type)}</span>
        <span className="truncate text-2xs text-neutral-500">
          {appName}
          {params ? ` · ${params}` : ""}
          {job.createdBy === "watchdog" ? " · otomatik" : ""}
        </span>
      </span>
      <span className="mono grid shrink-0 justify-items-end gap-0.5 text-2xs text-neutral-600">
        <span>{shortTime(job.createdAt)}</span>
        {duration && <span>{ACTIVE.has(job.status) ? `${duration} sürüyor` : duration}</span>}
      </span>
    </RowLink>
  );
}
